import React from "react";
import { useLanguage } from "../context/LanguageContext";

const PROTOCOLS = {
  fr: [
    {
      pathology: "Rupture de la coiffe des rotateurs",
      phases: [
        { when: "J+0", title: "Chirurgie arthroscopique", text: "Mini-incisions, gel de glace, antalgiques, attelle d'abduction." },
        { when: "J+1 à J+30", title: "Immobilisation protectrice", text: "Kinésithérapie passive douce. Pas de sollicitation active." },
        { when: "J+30 à J+90", title: "Mobilisation active", text: "Rééducation active assistée. Récupération des amplitudes." },
        { when: "J+90 à J+180", title: "Renforcement", text: "Travail de la force, proprioception, gestes spécifiques au sport." },
        { when: "M+6", title: "Reprise du sport", text: "Reprise progressive, d'abord en technique puis en compétition." },
      ],
    },
    {
      pathology: "Instabilité de l'épaule (Latarjet)",
      phases: [
        { when: "J+0", title: "Butée coracoïdienne", text: "Intervention ambulatoire. Écharpe de contention." },
        { when: "J+1 à J+30", title: "Consolidation osseuse", text: "Mobilisation douce. Écharpe la journée les 3 premières semaines." },
        { when: "J+30 à J+90", title: "Rééducation active", text: "Reprise des amplitudes complètes. Renforcement progressif." },
        { when: "J+90 à J+120", title: "Retour aux activités", text: "Sports sans contact autorisés. Préparation physique." },
        { when: "M+4", title: "Reprise sport contact", text: "Rugby, judo, arts martiaux — feu vert à 4 mois." },
      ],
    },
    {
      pathology: "Épicondylite — arthroscopie",
      phases: [
        { when: "J+0", title: "Arthroscopie mini-invasive", text: "Chirurgie ambulatoire. Pansement simple." },
        { when: "J+7", title: "Reprise des activités légères", text: "Cicatrisation. Retrait des fils. Reprise bureau/écriture." },
        { when: "J+14", title: "Kinésithérapie", text: "Étirements, massages, renforcement progressif des extenseurs." },
        { when: "J+45", title: "Activités manuelles", text: "Port de charges modérées autorisé. Surveillance des signes douloureux." },
        { when: "M+3", title: "Reprise du tennis", text: "Reprise progressive sur terrain synthétique, puis compétition." },
      ],
    },
    {
      pathology: "Canal carpien",
      phases: [
        { when: "J+0", title: "Neurolyse endoscopique", text: "Ambulatoire. Pansement 48 h. Reprise immédiate de l'autonomie." },
        { when: "J+7", title: "Retrait des fils", text: "Cicatrisation. Kinésithérapie non indispensable." },
        { when: "J+15", title: "Reprise du travail", text: "Travail de bureau : J+10. Travail manuel léger : J+21." },
        { when: "J+45", title: "Activités sportives", text: "Reprise totale : fitness, escalade, sports de raquette." },
        { when: "M+3", title: "Récupération complète", text: "Disparition des engourdissements nocturnes et de la perte de force." },
      ],
    },
    {
      pathology: "Prothèse d'épaule inversée",
      phases: [
        { when: "J+0", title: "Chirurgie", text: "Prothèse inversée sur mesure. Hospitalisation 2 à 3 nuits." },
        { when: "J+1 à J+30", title: "Protection articulaire", text: "Écharpe de contention. Rééducation passive." },
        { when: "J+30 à J+90", title: "Rééducation active", text: "Récupération progressive des amplitudes fonctionnelles." },
        { when: "M+3", title: "Activités quotidiennes", text: "Coiffage, cuisine, conduite autorisées — quasi-normale." },
        { when: "M+6", title: "Activités de loisirs", text: "Golf, natation, pétanque. Éviter sports de contact et charges > 5 kg." },
      ],
    },
  ],
  en: [
    {
      pathology: "Rotator cuff repair",
      phases: [
        { when: "D+0", title: "Arthroscopic surgery", text: "Mini incisions, ice, pain control, abduction brace." },
        { when: "D+1 to D+30", title: "Protective immobilization", text: "Gentle passive physio. No active loading." },
        { when: "D+30 to D+90", title: "Active mobilization", text: "Active-assisted rehab. Range-of-motion recovery." },
        { when: "D+90 to D+180", title: "Strengthening", text: "Strength, proprioception, sport-specific gestures." },
        { when: "M+6", title: "Return to sport", text: "Progressive return, technique first then competition." },
      ],
    },
    {
      pathology: "Shoulder instability (Latarjet)",
      phases: [
        { when: "D+0", title: "Coracoid transfer", text: "Day surgery. Sling." },
        { when: "D+1 to D+30", title: "Bone healing", text: "Gentle mobilization. Sling by day for 3 weeks." },
        { when: "D+30 to D+90", title: "Active rehab", text: "Full ROM recovery. Progressive strengthening." },
        { when: "D+90 to D+120", title: "Return to activity", text: "Non-contact sports allowed. Physical prep." },
        { when: "M+4", title: "Contact sport", text: "Rugby, judo, martial arts — green light at 4 months." },
      ],
    },
    {
      pathology: "Tennis elbow — arthroscopy",
      phases: [
        { when: "D+0", title: "Minimally-invasive arthroscopy", text: "Day surgery. Simple dressing." },
        { when: "D+7", title: "Light activities", text: "Wound healing. Stitch removal. Desk work / writing." },
        { when: "D+14", title: "Physiotherapy", text: "Stretching, massage, progressive extensor strengthening." },
        { when: "D+45", title: "Manual activities", text: "Moderate load allowed. Monitor pain signs." },
        { when: "M+3", title: "Tennis return", text: "Progressive return on synthetic court, then competition." },
      ],
    },
    {
      pathology: "Carpal tunnel",
      phases: [
        { when: "D+0", title: "Endoscopic release", text: "Day surgery. Dressing 48h. Immediate autonomy." },
        { when: "D+7", title: "Stitch removal", text: "Healing. Physio not required." },
        { when: "D+15", title: "Return to work", text: "Desk work: D+10. Light manual: D+21." },
        { when: "D+45", title: "Sports activities", text: "Full return: fitness, climbing, racquet sports." },
        { when: "M+3", title: "Full recovery", text: "Night numbness and grip weakness gone." },
      ],
    },
    {
      pathology: "Reverse shoulder prosthesis",
      phases: [
        { when: "D+0", title: "Surgery", text: "Custom reverse prosthesis. 2 to 3 nights hospitalization." },
        { when: "D+1 to D+30", title: "Joint protection", text: "Sling. Passive rehab." },
        { when: "D+30 to D+90", title: "Active rehab", text: "Progressive functional ROM recovery." },
        { when: "M+3", title: "Daily activities", text: "Grooming, cooking, driving — near-normal." },
        { when: "M+6", title: "Leisure activities", text: "Golf, swimming, boules. Avoid contact sports and > 5kg loads." },
      ],
    },
  ],
};

const RAACProtocol = () => {
  const { lang } = useLanguage();
  const protocols = PROTOCOLS[lang];
  const [active, setActive] = React.useState(0);

  return (
    <section
      id="raac"
      data-testid="raac-section"
      className="section-padding bg-white"
    >
      <div className="container-page">
        <div className="max-w-3xl">
          <span className="chip mb-5">
            {lang === "fr" ? "Protocole RAAC" : "Fast-recovery protocol"}
          </span>
          <h2 className="font-display font-black text-4xl sm:text-5xl text-navy leading-[1.05]">
            {lang === "fr"
              ? "Combien de temps pour revenir au sport ?"
              : "How long to get back to sport?"}
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">
            {lang === "fr"
              ? "Récupération Améliorée Après Chirurgie : un protocole personnalisé pour chaque pathologie, conçu pour accélérer votre retour aux activités en toute sécurité."
              : "Enhanced Recovery After Surgery: a personalized protocol for each condition, designed to accelerate your return safely."}
          </p>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-2" data-testid="raac-tabs">
          {protocols.map((p, i) => (
            <button
              key={i}
              type="button"
              data-testid={`raac-tab-${i}`}
              onClick={() => setActive(i)}
              className={`rounded-full border px-4 py-2 text-xs sm:text-sm font-display font-bold uppercase tracking-[0.08em] transition-all ${
                active === i
                  ? "bg-[#0A192F] text-white border-[#0A192F]"
                  : "bg-white text-navy border-border hover:border-[#FF5A1F]/40"
              }`}
            >
              {p.pathology}
            </button>
          ))}
        </div>

        {/* Timeline */}
        <div className="mt-10 relative">
          <div
            className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#FF5A1F] via-border to-transparent"
            aria-hidden="true"
          />
          <ol className="space-y-8">
            {protocols[active].phases.map((phase, i) => (
              <li
                key={i}
                data-testid={`raac-phase-${i}`}
                className={`relative pl-16 md:pl-0 md:grid md:grid-cols-2 md:gap-12 ${i % 2 === 0 ? "" : "md:[&>*:first-child]:order-2"}`}
              >
                <span className="absolute left-4 md:left-1/2 md:-translate-x-1/2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#FF5A1F] ring-4 ring-white shadow-md">
                  <span className="h-2 w-2 rounded-full bg-white"></span>
                </span>
                <div className={`md:px-6 ${i % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                  <p className="inline-block rounded-full bg-[#0A192F] text-white text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1">
                    {phase.when}
                  </p>
                </div>
                <div className={`mt-2 md:mt-0 md:px-6 ${i % 2 === 0 ? "md:text-left" : "md:text-right"}`}>
                  <h3 className="font-display text-lg font-bold text-navy">{phase.title}</h3>
                  <p className="mt-1.5 text-sm text-foreground/75 leading-relaxed">{phase.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <p className="mt-8 text-xs text-muted-foreground italic text-center max-w-2xl mx-auto">
          {lang === "fr"
            ? "Ces délais sont indicatifs. Chaque patient étant unique, les durées peuvent varier selon l'âge, l'état de santé et l'étendue de la pathologie."
            : "These timelines are indicative. Each patient being unique, durations may vary depending on age, general health and extent of pathology."}
        </p>
      </div>
    </section>
  );
};

export default RAACProtocol;
